import React, { useEffect, useRef, useState } from 'react';
import Papa from 'papaparse';
import * as XLSX from 'xlsx';
import Button from '../button';
import { useTheme } from '../../utils/theme-context';
import { useSiteWideSpeechStop } from '../../utils/audio-settings';
import { useSpeech } from '../../utils/speech-settings';


interface IAiMessages {
    text: string;
    files?: { name: string; url: string }[] | null;
    isNew?: boolean;
    data?: any;
    showActions?: boolean;
    considerDarkMode?: boolean;
    isTyping: boolean;
    uploadedFiles?: any;
    templateFiles?: any;
    showSubmitTemplate?: boolean;
    uploadedAll?: boolean;
    handleFileChange?: (e: any, section: string) => void;
    removeFile?: (section: string, index: number) => void;
    onClickOptions?: (option: any) => void;
    setIsTyping: (val: boolean) => void;
    onClickAction?: (action: string, id: string) => void;
    onClickLinks?: (type: string) => void;
    onClickSendFiles?: (files: any) => void;
    onFinishUploading?: () => void;
}

const AiMessages = ({
    text,
    files,
    isNew = false,
    isTyping,
    setIsTyping,
    data,
    showActions,
    considerDarkMode,
    uploadedFiles,
    templateFiles,
    showSubmitTemplate,
    uploadedAll,
    handleFileChange,
    removeFile,
    onClickOptions,
    onClickAction,
    onClickLinks,
    onFinishUploading
}: IAiMessages) => {
    const { theme } = useTheme();
    const cancelRef = useRef(false);
    const [previews, setPreviews] = useState<
        { name: string; url: string; type: string; preview?: any[][] }[]
    >([]);
    const [displayedText, setDisplayedText] = useState(isNew ? '' : text);
    const [isRichText, setIsRichText] = useState<boolean>(false);
    const isHTML = (input: string) => /<\/?[a-z][\s\S]*>/i.test(input);
    const filesTemplateArray = data?.stage === 'upload_template_files' ? data?.files && Object.keys(data?.files) : ['Bank System', 'POS']
    const { speak, stop, isSpeaking } = useSpeech();
    useSiteWideSpeechStop();

    useEffect(() => {
        if (!isNew) return;

        cancelRef.current = false;
        setIsTyping(true);
        setDisplayedText('');
        setIsRichText(isHTML(text));

        const splitHTML = (html: string) => {
            const regex = /(<[^>]+>|[^<]+)/g;
            return html?.match(regex) || [];
        };

        const chunks = isHTML(text) ? splitHTML(text) : [text];
        let fullOutput = '';
        let pendingTags = '';
        let chunkIndex = 0;
        let charIndex = 0;

        const typeNext = () => {
            if (cancelRef.current || chunkIndex >= chunks.length) {
                setIsTyping(false);
                return;
            }

            const currentChunk = chunks[chunkIndex];

            if (currentChunk?.startsWith('<')) {
                pendingTags += currentChunk;
                chunkIndex++;
                setTimeout(typeNext, 0);
            } else {
                if (charIndex < currentChunk?.length) {
                    if (pendingTags) {
                        fullOutput += pendingTags;
                        pendingTags = '';
                    }

                    fullOutput += currentChunk[charIndex];
                    charIndex++;
                    setDisplayedText(fullOutput);
                    setTimeout(typeNext, 40);
                } else {
                    chunkIndex++;
                    charIndex = 0;
                    setTimeout(typeNext, 0);
                }
            }
        };

        typeNext();

        return () => {
            cancelRef.current = true;
        };
    }, [isNew, text]);

    useEffect(() => {
        if (data?.stage === 'upload_template_files') return;
        if (!files || files?.length === 0) return;

        const loadPreviews = async () => {
            if (files && files?.length > 0) {
                const loaded = await Promise.all(
                    files?.map(async (file) => {
                        const fileName = typeof file?.url === 'string' ? file?.url?.split('/').pop() || '' : '';
                        const fileNameLower = fileName.toLowerCase();

                        const isCsv = fileNameLower.endsWith('.csv');
                        const isExcel = fileNameLower.endsWith('.xls') || fileNameLower.endsWith('.xlsx');
                        const isPdf = fileNameLower.endsWith('.pdf');
                        let preview: any[][] | undefined;

                        try {
                            const res = await fetch(file.url);

                            if (isCsv) {
                                const text = await res.text();
                                preview = Papa.parse(text, { preview: 5 }).data as any[][];
                            } else if (isExcel) {
                                const buffer = await res.arrayBuffer();
                                const wb = XLSX.read(buffer, { type: 'array' });
                                const sheet = wb.Sheets[wb.SheetNames[0]];
                                preview = XLSX.utils.sheet_to_json(sheet, { header: 1 }) as any[][];
                            }
                        } catch (err) {
                            console.error('Preview fetch failed:', err);
                        }

                        return {
                            name: file.name,
                            url: file.url,
                            type: isCsv ? 'csv' : isExcel ? 'excel' : isPdf ? 'pdf' : 'other',
                            preview,
                        };
                    })
                );

                setPreviews(loaded);
            }
        };

        loadPreviews();
    }, [files]);

    const getPlainText = (html: string): string => {
        const temp = document.createElement('div');
        temp.innerHTML = html;
        const plainText = temp.textContent || temp.innerText || '';

        if (!data?.options || data?.options?.length === 0) return plainText;

        const optionsText = data?.options?.map((opt: any) => opt?.key ? `${opt?.key}) ${opt?.value}` : ` ${opt?.value}`).join('\n');

        return `${plainText}\n\nOptions:\n${optionsText}`;
    };

    const handleClickSpeaking = () => {
        const plain = getPlainText(text);
        if (isSpeaking) {
            stop();
        } else {
            speak(plain, { lang: 'en-US' });
        }
    };

    const formatTemplateFilesName = (name: string) => {
        const namesToFormat: any = {
            'bank': 'Bank Statement',
            'pos': 'POS Sytem Export',
            'psp': 'PSP Report'
        }
        return namesToFormat?.[name] || name
    }

    return (
        <div className={`!rounded-xl m-2 py-2 px-3 bg-white ${considerDarkMode ? 'dark:bg-[#3a3a3a]' : ''} max-w-[85%]`}>
            <div className={`mb-2 whitespace-pre-wrap break-words ${considerDarkMode ? 'dark:text-primary-darkMode' : ''}`}>
                {isRichText ? (
                    <div dangerouslySetInnerHTML={{ __html: isTyping ? displayedText : text }} />
                ) : (
                    <p>{isTyping ? displayedText : text}</p>
                )}
            </div>

            {/* {isTyping && <span className='animate-pulse'>|</span>} */}

            {(data?.details && !isTyping) && (
                <Button
                    label={data?.details?.title}
                    className={`btn-primary-text-underline ${considerDarkMode ? 'dark:text-white' : ''}`}
                    onClickButton={() => onClickLinks?.(data?.details?.type)}
                />
            )}

            {(!isTyping && previews?.length > 0 && data?.stage !== 'upload_template_files') && (
                <div className='flex flex-wrap gap-2'>
                    {previews.map((file, idx) => {
                        const isImage = file.name.match(/\.(jpg|jpeg|png|gif|webp)$/i);

                        return (
                            <a
                                key={idx}
                                href={file.url}
                                download={file.name}
                                target='_blank'
                                rel='noopener noreferrer'
                                className={`relative border border-gray-300 rounded p-2 w-fit max-w-[300px] bg-gray-100 !text-white ${considerDarkMode ? 'dark:bg-gray-400 dark:text-black' : ''
                                    } hover:shadow-lg transition-all`}
                            >
                                {isImage ? (
                                    <img src={file.url} alt={file.name} className='w-24 h-24 object-cover rounded' />
                                ) : file.type === 'pdf' ? (
                                    <iframe src={file.url} title={file.name} className='w-48 h-32 border rounded' />
                                ) : file.preview ? (
                                    <div className='overflow-x-auto max-h-32 mb-2 border'>
                                        <table className='text-xs text-left border-collapse w-full'>
                                            <tbody>
                                                {file.preview.map((row, rowIdx) => (
                                                    <tr key={rowIdx}>
                                                        {row.map((cell, colIdx) => (
                                                            <td
                                                                key={colIdx}
                                                                className='border px-1 py-0.5 whitespace-nowrap text-black'
                                                            >
                                                                {cell}
                                                            </td>
                                                        ))}
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                ) : null}
                                <div
                                    className={`bg-[#252B37f0] text-white ${considerDarkMode ? 'dark:bg-gray-200 dark:text-black' : ''
                                        } text-center text-xs rounded-lg p-2 mt-1 truncate max-w-[250px]`}
                                >
                                    {file.name}
                                </div>
                            </a>
                        );
                    })}
                </div>
            )}
            {(!isTyping && filesTemplateArray && data?.stage === 'upload_template_files') && (
                <div className='flex flex-col gap-4'>
                    {filesTemplateArray?.map((item: any, index: number) => {
                        const isLastSingle = index === filesTemplateArray?.length - 1 && filesTemplateArray?.length % 2 !== 0;
                        let templateFilesForThis = templateFiles && templateFiles?.length > 0 && templateFiles?.filter((temp: any) => temp?.type === item)
                        if (isLastSingle) {
                            return (
                                <div key={index} className='w-full'>
                                    <div className='border rounded-md p-4 w-full h-full flex flex-col'>
                                        <h2 className='font-medium mb-2 dark:text-primary-darkMode'>{formatTemplateFilesName(item)}</h2>
                                        <div className='border-dashed border-2 border-gray-300 rounded-md cursor-pointer h-full flex flex-col justify-between p-4 relative'>
                                            {(uploadedFiles?.[item]?.length > 0 && !(templateFilesForThis && templateFilesForThis?.length > 0)) && (
                                                <div className='space-y-2 absolute top-0 bottom-0 right-0 left-0 bg-blue-900 flex flex-col justify-center rounded-md'>
                                                    {uploadedFiles?.[item].map((file: any, idx: number) => (
                                                        <div
                                                            key={idx}
                                                            className='flex justify-center items-center bg-blue-900 text-white text-sm rounded px-4 py-2'
                                                        >
                                                            <span className='truncate max-w-[80%]'>{file.name}</span>
                                                            <button onClick={() => removeFile?.(item, idx)} className='ml-2 hover:opacity-70'>
                                                                <img src={'/assets/chat/delete-icon.svg'} className='object-contain w-[20px]' />
                                                            </button>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                            {(templateFilesForThis && templateFilesForThis?.length > 0) && (
                                                <div className='space-y-2 absolute top-0 bottom-0 right-0 left-0 bg-blue-900 flex flex-col justify-center rounded-md'>
                                                    {templateFilesForThis?.map((file: any, idx: number) => {
                                                        return (
                                                            <div
                                                                key={idx}
                                                                className='flex justify-center items-center bg-blue-900 text-white text-sm rounded px-4 py-2'
                                                            >
                                                                <span className='truncate max-w-[80%]'>{file?.name}</span>
                                                            </div>
                                                        )
                                                    })}
                                                </div>
                                            )}
                                            <input
                                                type='file'
                                                multiple
                                                className='hidden'
                                                accept='.csv, .xls, .xlsx, .xml, .pdf, text/csv, application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, text/xml, application/xml, application/pdf'
                                                id={`file-${item}`}
                                                onChange={(e) => handleFileChange?.(e, item)}
                                            />
                                            <label htmlFor={`file-${item}`} className='cursor-pointer block'>
                                                <div className='mb-2 flex justify-center'>
                                                    <img
                                                        src={theme === 'dark'
                                                            ? '/assets/chat/upload-files-white.svg'
                                                            : '/assets/chat/upload-files.svg'}
                                                        className='object-contain w-[40px]'
                                                    />
                                                </div>
                                                <p className='text-primary-light dark:text-primary-darkMode text-center'>
                                                    Drop files here or click to browse
                                                </p>
                                                <p className='text-sm text-primary-light dark:text-primary-darkMode text-center'>
                                                    Supports .csv, .xlsx, .xml, .pdf
                                                </p>
                                            </label>
                                        </div>
                                    </div>
                                </div>
                            );
                        }

                        if (index % 2 === 0) {
                            const fieldOne = item;
                            const fieldTwo = filesTemplateArray[index + 1];
                            return (
                                <div key={index} className='flex flex-col sm:flex-row gap-4'>
                                    {[fieldOne, fieldTwo].map((field, i) => {
                                        templateFilesForThis = templateFiles && templateFiles?.length > 0 && templateFiles?.filter((temp: any) => temp?.type === field)
                                        return (
                                            <div key={i} className='w-full sm:w-1/2'>
                                                <div className='border rounded-md p-4 w-full h-full flex flex-col'>
                                                    <h2 className='font-medium mb-2 dark:text-primary-darkMode'>{formatTemplateFilesName(field)}</h2>
                                                    <div className='border-dashed border-2 border-gray-300 rounded-md cursor-pointer h-full flex flex-col justify-between p-4 relative'>
                                                        {(uploadedFiles?.[field] && uploadedFiles?.[field]?.length > 0 && !(templateFilesForThis && templateFilesForThis?.length > 0)) && (
                                                            <div className='space-y-2 absolute top-0 bottom-0 right-0 left-0 bg-blue-900 flex flex-col justify-center'>
                                                                {uploadedFiles?.[field].map((file: any, idx: number) => (
                                                                    <div
                                                                        key={idx}
                                                                        className='flex justify-center items-center bg-blue-900 text-white text-sm rounded px-4 py-2 !rounded-md'
                                                                    >
                                                                        <span className='truncate max-w-[80%]'>{file.name}</span>
                                                                        <button onClick={() => removeFile?.(field, idx)} className='ml-2 hover:opacity-70'>
                                                                            <img src={'/assets/chat/delete-icon.svg'} className='object-contain w-[20px]' />
                                                                        </button>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        )}
                                                        {(templateFilesForThis && templateFilesForThis?.length > 0) && (
                                                            <div className='space-y-2 absolute top-0 bottom-0 right-0 left-0 bg-blue-900 flex flex-col justify-center rounded-md'>
                                                                {templateFilesForThis?.map((file: any, idx: number) => {
                                                                    return (
                                                                        <div
                                                                            key={idx}
                                                                            className='flex justify-center items-center bg-blue-900 text-white text-sm rounded px-4 py-2'
                                                                        >
                                                                            <span className='truncate max-w-[80%]'>{file?.name}</span>
                                                                        </div>
                                                                    )
                                                                })}
                                                            </div>
                                                        )}
                                                        <input
                                                            type='file'
                                                            multiple
                                                            accept='.csv, .xls, .xlsx, .xml, .pdf, text/csv, application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, text/xml, application/xml, application/pdf'
                                                            className='hidden'
                                                            id={`file-${field}`}
                                                            onChange={(e) => handleFileChange?.(e, field)}
                                                        />
                                                        <label htmlFor={`file-${field}`} className='cursor-pointer block'>
                                                            <div className='mb-2 flex justify-center'>
                                                                <img
                                                                    src={theme === 'dark'
                                                                        ? '/assets/chat/upload-files-white.svg'
                                                                        : '/assets/chat/upload-files.svg'}
                                                                    className='object-contain w-[40px]'
                                                                />
                                                            </div>
                                                            <p className='text-primary-light dark:text-primary-darkMode text-center'>
                                                                Drop files here or click to browse
                                                            </p>
                                                            <p className='text-sm text-primary-light dark:text-primary-darkMode text-center'>
                                                                Supports .csv, .xlsx, .xml, .pdf
                                                            </p>
                                                        </label>
                                                    </div>
                                                </div>
                                            </div>
                                        )
                                    })}
                                </div>
                            );
                        }
                        return null;
                    })}
                    {showSubmitTemplate &&
                        <div className='my-5'>
                            <Button
                                label={'Submit'}
                                className={`${uploadedAll ? 'btn-primary-rounded-less dark:!bg-black' : 'btn-primary-rounded-less-disable !cursor-not-allowed'} min-w-[150px]`}
                                onClickButton={() => uploadedAll && onFinishUploading?.()}
                            />
                        </div>
                    }
                </div>
            )}
            {(data?.options?.length > 0 && !isTyping) &&
                <div className='flex flex-col items-start'>
                    {data?.options?.map((item: any, idx: number) => {
                        return (
                            <div key={idx}
                                onClick={() => onClickOptions?.(item)}
                                className='flex flex-row items-center cursor-pointer hover:opacity-80 px-4 py-2 my-1 bg-[#252B37] dark:bg-black rounded-full'>
                                {item?.key ?
                                    <p className='text-white font-bold mr-2'>{item?.key}.</p>
                                    : <div className='w-2 h-2 bg-white rounded-full mr-2'></div>
                                }
                                <p className='text-white text-md'>{item?.value}</p>
                            </div>
                        )
                    })
                    }
                </div>
            }
            {text && !isTyping && showActions && (
                <div className='mt-4 flex flex-row items-center'>
                    <div
                        onClick={() => {
                            const plainText = getPlainText(text);
                            navigator?.clipboard?.writeText(plainText);
                        }}
                        className='hover:opacity-80 cursor-pointer'
                        title='Copy'
                    >
                        <img
                            src={theme === 'dark' ? '/assets/chat/copy-icon-white.svg' : '/assets/chat/copy-icon.svg'}
                            className='object-contain h-[20px] mr-2'
                        />
                    </div>

                    {data?._id && (
                        <>
                            <div className='hover:opacity-80 cursor-pointer' title='Like' onClick={() => onClickAction?.('like', data._id)}>
                                <img
                                    src={
                                        data?.liked
                                            ? theme === 'dark' ? '/assets/chat/like-selected-icon-white.svg' : '/assets/chat/like-selected-icon.svg'
                                            : theme === 'dark' ? '/assets/chat/like-icon-white.svg' : '/assets/chat/like-icon.svg'
                                    }
                                    className='object-contain h-[20px] mr-2'
                                />
                            </div>
                            <div className='hover:opacity-80 cursor-pointer' title='Dislike' onClick={() => onClickAction?.('dislike', data._id)}>
                                <img
                                    src={
                                        data?.disliked
                                            ? theme === 'dark' ? '/assets/chat/dislike-selected-icon-white.svg' : '/assets/chat/dislike-selected-icon.svg'
                                            : theme === 'dark' ? '/assets/chat/dislike-icon-white.svg' : '/assets/chat/dislike-icon.svg'
                                    }
                                    className='object-contain h-[20px] mr-2'
                                />
                            </div>
                            <div className='hover:opacity-80 cursor-pointer' title='Favorite' onClick={() => onClickAction?.('favorite', data._id)}>
                                <img
                                    src={
                                        data?.favorite
                                            ? theme === 'dark' ? '/assets/chat/favorite-selected-icon-white.svg' : '/assets/chat/favorite-selected-icon.svg'
                                            : theme === 'dark' ? '/assets/chat/favorite-icon-white.svg' : '/assets/chat/favorite-icon.svg'
                                    }
                                    className='object-contain h-[22px] mr-2'
                                />
                            </div>
                        </>
                    )}
                    <div className='hover:opacity-80 cursor-pointer relative' title={isSpeaking ? 'Stop reading' : 'Read aloud'} onClick={handleClickSpeaking}>
                        <img
                            src={
                                isSpeaking
                                    ? theme === 'dark' && considerDarkMode
                                        ? '/assets/chat/audio-selected-icon-white.svg'
                                        : '/assets/chat/audio-selected-icon.svg'
                                    : theme === 'dark' && considerDarkMode
                                        ? '/assets/chat/audio-icon-white.svg'
                                        : '/assets/chat/audio-icon.svg'
                            }
                            className={`object-contain h-[20px] mr-2 ${isSpeaking ? 'animate-pulse' : ''}`}
                        />
                        {isSpeaking && (
                            <div className='absolute top-[-4px] right-[-4px] bg-[#252B37] dark:bg-gray-300 rounded-full w-2.5 h-2.5 animate-ping'></div>
                        )}
                    </div>
                </div>
            )}

        </div>
    );
};

export default AiMessages;